import { utcToLocal, REFUND_METHOD, TRANSACTION_STATUSES } from '@/utility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useMemo, useState } from 'react';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';
import { PAYMENT_METHODS } from '@/utility';
import TransferACHInformation from '@/components/TransferACHInformation';
import { Box } from '@mui/material';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    borderBottomWidth: '0.5px',
    borderBottomColor: 'rgb(224, 224, 224)',
  },
}));

const getRefundMethodString = (refundMethod) => {
  return PAYMENT_METHODS.find((method) => method.value === refundMethod)?.text;
};

export const StatusHistoryHeaderRow = (props) => {
  return (
    <TableRow>
      <StyledTableCell />
      <StyledTableCell align='center'>
        Updated Date
        <br /> / Time
      </StyledTableCell>
      <StyledTableCell align='center'>Payment Type</StyledTableCell>
      <StyledTableCell align='center'>
        Approval/Payment/Refund Status
      </StyledTableCell>
      {props.role === 'admin' && (
        <StyledTableCell align='center'>Transfer Status</StyledTableCell>
      )}
      {props.role === 'admin' && (
        <StyledTableCell align='right'>External_id</StyledTableCell>
      )}
      <StyledTableCell align='center'>Audience</StyledTableCell>
      <StyledTableCell align='center'>Updated By</StyledTableCell>
      <StyledTableCell align='center'>Comment</StyledTableCell>
      {props.role === 'admin' && (
        <StyledTableCell align='center'>Error</StyledTableCell>
      )}
    </TableRow>
  );
};

export const StatusHistoryRow = (props) => {
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();

  const ACH_RETURN_TEXT = 'Payment ACH-returned';
  const PAYMENT_PENDING_TEXT = 'Payment Pending';
  const PAYMENT_SUCCESS = 'Payment Success';
  const COMPLETED = 'Completed';

  const shouldHaveAchDetails =
    (props.obj?.refund_status === ACH_RETURN_TEXT ||
      props.obj?.refund_status === PAYMENT_PENDING_TEXT ||
      props.obj?.refund_status === PAYMENT_SUCCESS) &&
    props.obj?.refund_method === REFUND_METHOD.ACH;

  const isExpandable =
    Boolean(props.obj?.source_ach_details) ||
    Boolean(props.obj?.destination_ach_details) ||
    (Boolean(props.obj?.destination_bank_account) &&
      props.obj?.refund_method === REFUND_METHOD.ACH);

  const [open, setOpen] = useState(false);

  const handleExpand = () => {
    if (isExpandable) {
      setOpen(!open);
    }
  };

  const refundStatus = useMemo(() => {
    if (props.obj.refund_status === ACH_RETURN_TEXT) {
      return (
        <Chip
          label={`Payment ${getTranslatedStatus(
            TRANSACTION_STATUSES.ACH_RETURNED,
          )}`}
          variant='filled'
          color='error'
        />
      );
    }
    if (props.obj.refund_status === PAYMENT_PENDING_TEXT) {
      return (
        <Chip
          label={`Payment ${getTranslatedStatus(TRANSACTION_STATUSES.PENDING)}`}
          variant='outlined'
          color='warning'
        />
      );
    }
    if (props.obj.refund_status === PAYMENT_SUCCESS) {
      return (
        <Chip
          label={`Payment ${getTranslatedStatus(TRANSACTION_STATUSES.SUCCESS)}`}
          variant='outlined'
          color='success'
        />
      );
    }
    if (props.obj.refund_status === COMPLETED) {
      return <Chip label={COMPLETED} variant='filled' color='success' />;
    }
    if (props.obj.refund_status?.startsWith('Payment ')) {
      const statusPart = props.obj.refund_status.replace('Payment ', '');
      const translatedStatus = getTranslatedStatus(statusPart);

      if (
        statusPart === TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK
      ) {
        return (
          <Chip
            label={`Payment ${translatedStatus}`}
            variant='outlined'
            color='warning'
          />
        );
      }

      if (statusPart === TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM) {
        return (
          <Chip
            label={`Payment ${translatedStatus}`}
            variant='outlined'
            color='success'
          />
        );
      }

      return (
        <Chip
          label={`Payment ${translatedStatus}`}
          variant='outlined'
          color='default'
        />
      );
    }
    return (
      <Chip
        label={props.obj.refund_status || '-'}
        variant='outlined'
        color='default'
      />
    );
  }, [props.obj.refund_status, getTranslatedStatus]);

  const expandedColSpan = props.role === 'admin' ? 10 : 7;
  const destinationBankAccount = props.obj?.destination_bank_account;
  const sourceAchDetails = props.obj?.source_ach_details;
  const destinationAchDetails = props.obj?.destination_ach_details;

  return (
    <>
      <TableRow onClick={handleExpand}>
        <StyledTableCell align='center'>
          {isExpandable && (
            <IconButton
              aria-label='expand row'
              size='small'
              onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          {shouldHaveAchDetails && props.role === 'admin' && !isExpandable && (
            <Button
              onClick={() => props.onRetryPullingAch(props.obj)}
              variant='outlined'
              color='warning'
              size='small'>
              Retry Pulling ACH-Data
            </Button>
          )}
        </StyledTableCell>
        <StyledTableCell align='center'>
          {utcToLocal(props.obj.created_at, 'MM-DD-YY')}
          <br />
          {utcToLocal(props.obj.created_at, 'hh:mm A')}
        </StyledTableCell>

        <StyledTableCell align='center'>
          {getRefundMethodString(props.obj.refund_method) ?? 'N/A'} <br />
          {props.obj.additional_info
            ? `(${props.obj.additional_info})`
            : ''}{' '}
        </StyledTableCell>
        <StyledTableCell align='center'>
          {refundStatus} <br />
        </StyledTableCell>
        {props.role === 'admin' && (
          <StyledTableCell align='center'>
            {props.obj.transfer_status
              ? getTranslatedStatus(props.obj.transfer_status)
              : 'N/A'}
          </StyledTableCell>
        )}
        {props.role === 'admin' && (
          <StyledTableCell align='right'>
            {props.obj.external_id}
          </StyledTableCell>
        )}
        <StyledTableCell align='center'>
          {props.obj.audienceName || 'N/A'}
        </StyledTableCell>
        <StyledTableCell align='center'>{props.obj.updated_by}</StyledTableCell>
        <StyledTableCell align='center'>{props.obj.comment}</StyledTableCell>
        {props.role === 'admin' && (
          <StyledTableCell align='center'>
            {props.obj.error_message ? props.obj.error_message : '-'}
          </StyledTableCell>
        )}
      </TableRow>

      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <StyledTableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={expandedColSpan}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ p: 1.5 }}>
              <TransferACHInformation
                transferId={props.obj?.transfer_id}
                destinationBankAccount={destinationBankAccount}
                sourceAchDetails={sourceAchDetails}
                destinationAchDetails={destinationAchDetails}
              />
            </Box>
          </Collapse>
        </StyledTableCell>
      </TableRow>
    </>
  );
};
